import { useState, useEffect } from "react";
import { Link, NavLink } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { Logo } from "@/components/Logo";
import { NAV } from "@/lib/data";
import { useQuote } from "@/context/QuoteContext";

export const Header = () => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { openQuote } = useQuote();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [open]);

  return (
    <header
      data-testid="site-header"
      className={`sticky top-0 z-50 bg-white transition-shadow ${scrolled ? "shadow-[0_2px_20px_rgba(10,35,66,0.08)]" : ""}`}
    >
      <div className="mx-auto max-w-[1200px] px-5 lg:px-8">
        <div className="flex h-[74px] items-center justify-between">
          <Link to="/" data-testid="logo-home-link" aria-label="Solar Pros Cleaning Services home">
            <Logo />
          </Link>

          <nav className="hidden lg:flex items-center gap-9">
            {NAV.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.end}
                data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
                className={({ isActive }) =>
                  `relative font-display text-[14px] font-semibold tracking-wide transition-colors ${
                    isActive ? "text-[#1E5EDB]" : "text-[#0A2342] hover:text-[#1E5EDB]"
                  }`
                }
              >
                {({ isActive }) => (
                  <>
                    {item.label}
                    <span
                      className={`absolute -bottom-2 left-0 h-[2.5px] w-full origin-left rounded-full bg-[#1E5EDB] transition-transform duration-300 ${
                        isActive ? "scale-x-100" : "scale-x-0"
                      }`}
                    />
                  </>
                )}
              </NavLink>
            ))}
          </nav>

          <div className="flex items-center gap-3">
            <button
              onClick={openQuote}
              data-testid="header-quote-btn"
              className="hidden sm:inline-flex items-center justify-center rounded-md bg-[#1E5EDB] px-5 py-2.5 font-display text-[13.5px] font-bold tracking-wide text-white transition-colors hover:bg-[#1A52C2]"
            >
              GET A QUOTE
            </button>
            <button
              onClick={() => setOpen(true)}
              data-testid="mobile-menu-open"
              aria-label="Open menu"
              className="lg:hidden text-[#0A2342] p-1"
            >
              <Menu size={26} />
            </button>
          </div>
        </div>
      </div>

      {/* Mobile drawer */}
      <div
        className={`fixed inset-0 z-50 lg:hidden transition-opacity ${open ? "opacity-100" : "pointer-events-none opacity-0"}`}
      >
        <div className="absolute inset-0 bg-[#0A2342]/50" onClick={() => setOpen(false)} />
        <div
          className={`absolute right-0 top-0 h-full w-[82%] max-w-[340px] bg-white p-6 shadow-xl transition-transform duration-300 ${open ? "translate-x-0" : "translate-x-full"}`}
        >
          <div className="flex items-center justify-between">
            <Logo />
            <button onClick={() => setOpen(false)} data-testid="mobile-menu-close" aria-label="Close menu" className="text-[#0A2342]">
              <X size={26} />
            </button>
          </div>
          <nav className="mt-10 flex flex-col gap-1">
            {NAV.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.end}
                onClick={() => setOpen(false)}
                data-testid={`mobile-nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
                className={({ isActive }) =>
                  `rounded-lg px-4 py-3 font-display text-[16px] font-semibold tracking-wide ${
                    isActive ? "bg-[#F4F7FB] text-[#1E5EDB]" : "text-[#0A2342]"
                  }`
                }
              >
                {item.label}
              </NavLink>
            ))}
            <button
              onClick={() => { setOpen(false); openQuote(); }}
              data-testid="mobile-quote-btn"
              className="mt-4 inline-flex items-center justify-center rounded-md bg-[#1E5EDB] px-5 py-3 font-display font-bold tracking-wide text-white"
            >
              GET A QUOTE
            </button>
          </nav>
        </div>
      </div>
    </header>
  );
};
